import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { fetchWithAuthRetry } from '../utils/withTimeout';

const Leaderboard = ({ currentUser, authReady }) => {
  const [sport, setSport] = useState('ironman');
  const [filter, setFilter] = useState('streak');
  const [allPlayers, setAllPlayers] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      // Page through ALL profiles - a single .limit(1000) fetch silently drops
      // every player past row 1000 (uuid order), so users vanish from the
      // leaderboard as signups grow.
      //
      // PostgREST caps a page at 1000 server-side, so paging is unavoidable,
      // but the pages don't depend on each other: get the count first, then
      // fetch every page CONCURRENTLY. The old sequential while-loop cost one
      // full round trip per 1000 signups (crossing 2000 users quietly added a
      // third), which is what made this feel slower as the base grew.
      // wins/losses/games_played were selected but never rendered here - drop
      // them; the key names repeat on every row, so they were pure payload.
      const COLS = 'id, username, current_streak, longest_streak, nba_current_streak, nba_longest_streak, mosh_current_streak, mosh_longest_streak, ntg_current_streak, ntg_longest_streak, ntg_sr_current_streak, ntg_sr_longest_streak, digits_current_streak, digits_longest_streak, ironman_current_streak, ironman_longest_streak';
      const PAGE = 1000;

      const { count, error: countErr } = await fetchWithAuthRetry(() => supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true }), { ms: 4000 });
      if (countErr) throw countErr;

      const pageCount = Math.max(1, Math.ceil((count || 0) / PAGE));
      const pages = await Promise.all(
        Array.from({ length: pageCount }, (_, i) => fetchWithAuthRetry(() => supabase
          .from('profiles')
          .select(COLS)
          .order('id', { ascending: true })
          .range(i * PAGE, i * PAGE + PAGE - 1), { ms: 4000 }))
      );
      const bad = pages.find(p => p.error);
      if (bad) throw bad.error;
      const players = pages.flatMap(p => p.data || []);

      setAllPlayers(players);
      setStatus('ok');
    } catch (err) {
      console.error('Leaderboard load error:', err);
      setStatus('error');
    }
  }, []);

  useEffect(() => { if (authReady) load(); }, [authReady, load]);

  const getScore = (p) => {
  if (sport === 'ironman') return filter === 'streak' ? (p.ironman_current_streak || 0) : (p.ironman_longest_streak || 0);
  if (sport === 'nfl') return filter === 'streak' ? p.current_streak : p.longest_streak;
  if (sport === 'nba') return filter === 'streak' ? (p.nba_current_streak || 0) : (p.nba_longest_streak || 0);
  if (sport === 'ntg') return filter === 'streak' ? (p.ntg_current_streak || 0) : (p.ntg_longest_streak || 0);
  if (sport === 'ntg_sr') return filter === 'streak' ? (p.ntg_sr_current_streak || 0) : (p.ntg_sr_longest_streak || 0);
  if (sport === 'digits') return filter === 'streak' ? (p.digits_current_streak || 0) : (p.digits_longest_streak || 0);
  return filter === 'streak' ? (p.mosh_current_streak || 0) : (p.mosh_longest_streak || 0);
};

  const getEntries = () => {
    return [...allPlayers]
      .filter(p => getScore(p) > 0)
      .sort((a, b) => getScore(b) - getScore(a))
      .slice(0, 25);
  };

  const entries = getEntries();

  // Signed-in user's rank across the FULL qualifying pool (not just the top-25
  // slice), same competition-ranking + tie rules as the rows. Never exposes the
  // total player count. null -> chip hidden (signed out, or no streak here).
  const myRank = (() => {
    if (!currentUser) return null;
    const me = allPlayers.find(p => p.id === currentUser.id);
    if (!me) return null;
    const myScore = getScore(me);
    if (myScore <= 0) return null;
    const qualified = allPlayers.filter(p => getScore(p) > 0);
    const rank = qualified.filter(p => getScore(p) > myScore).length + 1;
    const tie = qualified.filter(p => getScore(p) === myScore).length > 1;
    return tie ? `T-${rank}` : `${rank}`;
  })();

  const tabStyle = (active) => ({
    flex: '1 1 0',
    minWidth: 0,
    padding: '6px 2px',
    borderRadius: '3px',
    border: `1px solid ${active ? '#c41e3a' : '#2a3a55'}`,
    background: active ? '#c41e3a' : 'transparent',
    color: active ? 'white' : '#5a6a80',
    fontFamily: 'var(--font-heading)',
    fontSize: '10px',
    letterSpacing: '0px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  });

  return (
    <div className="leaderboard">
      <div className="leaderboard-header">
        {myRank && (
          <div className="lb-myrank">
            <div className="lb-myrank-label">RANK</div>
            <div className="lb-myrank-value">{myRank}</div>
          </div>
        )}
        <h2 className="leaderboard-title">LEADERBOARD</h2>
        <div className="title-stripe small" />
      </div>

      {/* Sport selector */}
      <div style={{ display: 'flex', gap: '4px', padding: '8px 8px 0', justifyContent: 'center' }}>
        <button style={tabStyle(sport === 'ironman')} onClick={() => { setSport('ironman'); setFilter('streak'); }}>🧢 LIDS</button>
        <button style={tabStyle(sport === 'nfl')} onClick={() => setSport('nfl')}>🏈 NFL</button>
        <button style={tabStyle(sport === 'nba')} onClick={() => setSport('nba')}>🏀 NBA</button>
        <button style={tabStyle(sport === 'mosh')} onClick={() => setSport('mosh')}>🍲 Mosh</button>
        <button style={tabStyle(sport === 'ntg_sr')} onClick={() => setSport('ntg_sr')}>👴 Sr.</button>
        <button style={tabStyle(sport === 'ntg')} onClick={() => setSport('ntg')}>👶 Jr.</button>
        <button style={tabStyle(sport === 'digits')} onClick={() => setSport('digits')}>#️⃣ Digits</button>
      </div>

      {/* Streak type selector */}
      {sport === 'ironman' ? (
        <div style={{ textAlign: 'center', padding: '8px 16px 0', fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', lineHeight: '1.4' }}>
          <strong style={{ color: 'var(--gold)', fontFamily: 'var(--font-heading)', letterSpacing: '1px' }}>Locked in Daily Streak</strong><br />
          {'Complete any daily (NFL, NBA, Mish Mosh, Sr., Jr., or Digits) to stay alive. Pass or fail... just show up.'}</div>
      ) : (
        <>
          {(sport === 'ntg' || sport === 'ntg_sr') && (
            <div style={{ textAlign: 'center', padding: '8px 16px 0' }}>
              <strong style={{ color: 'var(--gold)', fontFamily: 'var(--font-heading)', letterSpacing: '1px', fontSize: '12px' }}>{sport === 'ntg_sr' ? 'Name That Guy Sr.' : 'Name That Guy Jr.'}</strong>
            </div>
          )}
          <div className="leaderboard-filters">
            <button
              className={`lb-filter-btn ${filter === 'streak' ? 'active' : ''}`}
              onClick={() => setFilter('streak')}
            >
              CURRENT STREAK
            </button>
            <button
              className={`lb-filter-btn ${filter === 'best' ? 'active' : ''}`}
              onClick={() => setFilter('best')}
            >
              ALL TIME
            </button>
          </div>
        </>
      )}

      <p style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--gold)', letterSpacing: '2px', textAlign: 'center', marginBottom: '4px' }}>
        TOP 25 — {sport === 'ironman' ? 'LIDS' : sport === 'digits' ? 'DIGITS' : sport === 'ntg_sr' ? 'NAME THAT GUY SR.' : sport === 'ntg' ? 'NAME THAT GUY JR.' : sport === 'nfl' ? 'NFL' : sport === 'nba' ? 'NBA' : 'NOTABLE ALUMNI'} {sport === 'ironman' ? 'CURRENT STREAKS' : filter === 'streak' ? 'CURRENT STREAKS' : 'ALL TIME STREAKS'}
      </p>

      {status === 'loading' ? (
        <div className="leaderboard-empty">Loading leaderboard...</div>
      ) : status === 'error' ? (
        <div className="leaderboard-empty">
          Couldn't load the leaderboard.{' '}
          <button onClick={load} style={{ background: 'none', border: 'none', color: 'var(--gold)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>Retry</button>
        </div>
      ) : entries.length === 0 ? (
        <div className="leaderboard-empty">No {sport === 'ironman' ? 'LIDS' : sport === 'digits' ? 'DIGITS' : sport === 'ntg_sr' ? 'NAME THAT GUY SR.' : sport === 'ntg' ? 'NAME THAT GUY JR.' : sport === 'mosh' ? 'NOTABLE ALUMNI' : sport.toUpperCase()} scores yet. {sport === 'ironman' ? 'Complete any daily challenge to start your streak!' : 'Play a game!'}</div>
      ) : (
        <div className="leaderboard-table">
          <div className="lb-row lb-head">
            <span className="lb-rank">#</span>
            <span className="lb-user">PLAYER</span>
            <span className="lb-score">{filter === 'streak' ? 'HOT STREAK' : 'ALL TIME'}</span>
          </div>
          <div className="lb-scroll">
            {entries.map((p, i) => {
              const score = getScore(p);
              const isMe = currentUser && p.id === currentUser.id;
              const rank = entries.filter(e => getScore(e) > score).length + 1;
              const isTie = entries.filter(e => getScore(e) === score).length > 1;
              return (
                <div
                  key={p.id}
                  className={`lb-row ${isMe ? 'lb-me' : ''} ${i === 0 ? 'lb-gold' : i === 1 ? 'lb-silver' : i === 2 ? 'lb-bronze' : ''}`}
                >
                  <span className="lb-rank">{isTie ? `T-${rank}` : rank}</span>
                  <span className="lb-user">
                    {p.username || 'Anonymous'}
                    {isMe && <span className="lb-you-badge">YOU</span>}
                  </span>
                  <span className="lb-score">
                    <strong>{score}</strong>{' '}
                    <span className="lb-pct">{score === 1 ? 'day' : 'days'}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;